import React, { useEffect, useState, useCallback } from 'react';
import { Upload, Trash2, Folder, FolderPlus, ChevronRight, ChevronDown, Edit2, X, Move, ZoomIn, CheckSquare, Square, Check, FileText, Image, Copy, Link, RefreshCw, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../utils/api';
import Sidebar from '../components/Sidebar';

const MediaLibrary = () => {
  const [media, setMedia] = useState([]);
  const [folders, setFolders] = useState([]);
  const [tenantPublicOrigin, setTenantPublicOrigin] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [dragOver, setDragOver] = useState(false);
  
  // Modal states
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [showEditMetadataModal, setShowEditMetadataModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderParentId, setNewFolderParentId] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [editingMedia, setEditingMedia] = useState(null);
  const [mediaMetadata, setMediaMetadata] = useState({ alt_text: '', title: '', caption: '' });
  const [savingMetadata, setSavingMetadata] = useState(false);
  // Custom URL slug editing
  const [slugInput, setSlugInput] = useState('');
  const [savingSlug, setSavingSlug] = useState(false);
  
  // Multi-select state
  const [selectedMediaIds, setSelectedMediaIds] = useState(new Set());
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);

  // Search query — matched case-insensitively against filename, original_filename,
  // alt_text, title, caption and custom_slug. When non-empty, the listing
  // switches from folder-scoped to "everywhere" so users can find a file they
  // can't remember the location of.
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination — root listing and folder views show 60 items per page.
  // Resets to page 1 whenever the user switches folders or starts searching
  // so they always land on the most-recent items first.
  const PAGE_SIZE = 60;
  const [currentPage, setCurrentPage] = useState(1);

  // Sort order — backend returns newest-first by default. Editors can override
  // to find files by name or size. Persisted to localStorage so it sticks
  // across reloads.
  const [sortOrder, setSortOrder] = useState(() => {
    try { return localStorage.getItem('nexuscms_media_sort') || 'newest'; }
    catch (e) { return 'newest'; }
  });
  useEffect(() => {
    try { localStorage.setItem('nexuscms_media_sort', sortOrder); }
    catch (e) { /* ignore */ }
  }, [sortOrder]);

  useEffect(() => {
    setCurrentPage(1);
  }, [currentFolderId, searchQuery, sortOrder]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [mediaRes, foldersRes, tenantRes] = await Promise.all([
        api.get('/media'),
        api.get('/media/folders'),
        api.get('/tenant'),
      ]);
      setMedia(mediaRes.data);
      setFolders(foldersRes.data);
      // Use the tenant's custom domain as the public origin for copy URLs so
      // the copied link always reflects the tenant's own site, not the admin platform domain.
      const t = tenantRes.data;
      if (t?.custom_domain) {
        setTenantPublicOrigin(`https://${t.custom_domain}`);
      } else if (t?.subdomain) {
        // Fall back to the platform subdomain (e.g. https://eu.nexuscms.ai/site/slug)
        // Extract origin from window.location so we use the correct regional server
        setTenantPublicOrigin(window.location.origin);
      }
    } catch (error) {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = useCallback(async (files, folderId = currentFolderId) => {
    if (!files || files.length === 0) return;
    const filesArray = Array.from(files); // snapshot before async ops — FileList can read as 0 later
    const count = filesArray.length;

    setUploading(true);
    const uploadPromises = filesArray.map(async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      if (folderId) {
        formData.append('folder_id', folderId);
      }
      return api.post(`/media/upload${folderId ? `?folder_id=${folderId}` : ''}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    });

    try {
      await Promise.all(uploadPromises);
      toast.success(`${count} file(s) uploaded successfully`);
      fetchData();
    } catch (error) {
      toast.error('Failed to upload some files');
    } finally {
      setUploading(false);
    }
  }, [currentFolderId]);

  const handleFileInputChange = (e) => {
    handleUpload(e.target.files);
    e.target.value = '';
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleUpload(files);
    }
  }, [handleUpload]);

  const handleDelete = async (mediaId) => {
    if (!confirm('Delete this media file?')) return;

    try {
      await api.delete(`/media/${mediaId}`);
      toast.success('Media deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete media');
    }
  };

  // Folder operations
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }

    try {
      await api.post('/media/folders', {
        name: newFolderName,
        parent_id: newFolderParentId
      });
      toast.success('Folder created');
      setShowNewFolderModal(false);
      setNewFolderName('');
      setNewFolderParentId(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to create folder');
    }
  };

  const handleRenameFolder = async () => {
    if (!newFolderName.trim() || !selectedFolder) return;

    try {
      await api.put(`/media/folders/${selectedFolder.folder_id}`, {
        name: newFolderName
      });
      toast.success('Folder renamed');
      setShowRenameFolderModal(false);
      setNewFolderName('');
      setSelectedFolder(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to rename folder');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!confirm('Delete this folder? Files inside will be moved to root.')) return;

    try {
      await api.delete(`/media/folders/${folderId}`);
      toast.success('Folder deleted');
      if (currentFolderId === folderId) {
        setCurrentFolderId(null);
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to delete folder');
    }
  };

  const handleMoveMedia = async (targetFolderId) => {
    // Handle multi-select move using bulk endpoint
    if (selectedMediaIds.size > 0) {
      try {
        const response = await api.post('/media/move-bulk', {
          media_ids: Array.from(selectedMediaIds),
          folder_id: targetFolderId || null
        });
        toast.success(response.data.message || `${selectedMediaIds.size} file(s) moved`);
        setShowMoveModal(false);
        setSelectedMediaIds(new Set());
        setIsMultiSelectMode(false);
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to move files');
      }
      return;
    }
    
    // Handle single move
    if (!selectedMedia) return;

    try {
      await api.put(`/media/${selectedMedia.media_id}/move`, null, {
        params: { folder_id: targetFolderId || null }
      });
      toast.success('Media moved');
      setShowMoveModal(false);
      setSelectedMedia(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to move media');
    }
  };

  // Open edit metadata modal
  const handleEditMetadata = (mediaItem) => {
    setEditingMedia(mediaItem);
    setMediaMetadata({
      alt_text: mediaItem.alt_text || '',
      title: mediaItem.title || '',
      caption: mediaItem.caption || ''
    });
    setSlugInput(mediaItem.custom_slug || '');
    setShowEditMetadataModal(true);
  };

  // Save media metadata
  const handleSaveMetadata = async () => {
    if (!editingMedia) return;
    
    setSavingMetadata(true);
    try {
      const response = await api.put(`/media/${editingMedia.media_id}`, mediaMetadata);
      
      // Update local state
      setMedia(prev => prev.map(m => 
        m.media_id === editingMedia.media_id 
          ? { ...m, ...mediaMetadata }
          : m
      ));
      
      toast.success('Image metadata saved');
      setShowEditMetadataModal(false);
      setEditingMedia(null);
    } catch (error) {
      toast.error('Failed to save metadata');
    } finally {
      setSavingMetadata(false);
    }
  };

  // Save the custom URL slug for a document. Old slug becomes a redirect.
  const handleSaveSlug = async () => {
    if (!editingMedia) return;
    const trimmed = (slugInput || '').trim();
    if (!trimmed) {
      toast.error('Custom URL cannot be empty');
      return;
    }
    setSavingSlug(true);
    try {
      const res = await api.put(`/media/${editingMedia.media_id}/slug`, {
        custom_slug: trimmed,
      });
      const newSlug = res.data?.custom_slug || trimmed;
      setMedia(prev => prev.map(m =>
        m.media_id === editingMedia.media_id
          ? { ...m, custom_slug: newSlug, slug_history: res.data?.slug_history || m.slug_history }
          : m
      ));
      setEditingMedia(prev => prev ? { ...prev, custom_slug: newSlug, slug_history: res.data?.slug_history || prev.slug_history } : prev);
      setSlugInput(newSlug);
      toast.success('Custom URL updated');
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Failed to update custom URL';
      toast.error(detail);
    } finally {
      setSavingSlug(false);
    }
  };

  // Return the URL we want customers to copy/share for a media item.
  // Prefer the pretty /documents/{slug} URL when a custom_slug exists,
  // otherwise fall back to the raw storage URL.
  // Always use the tenant's own public domain (custom_domain if set) so the
  // copied link says esha.org/..., not eu.nexuscms.ai/...
  const getPreferredUrl = (item) => {
    if (item?.custom_slug) {
      const base = tenantPublicOrigin || window.location.origin;
      return `${base}/documents/${item.custom_slug}`;
    }
    return item?.url || '';
  };

  // Multi-select handlers
  const toggleMediaSelection = (mediaId) => {
    setSelectedMediaIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(mediaId)) {
        newSet.delete(mediaId);
      } else {
        newSet.add(mediaId);
      }
      return newSet;
    });
  };

  const selectAllInFolder = () => {
    const allIds = filteredMedia.map(item => item.media_id);
    setSelectedMediaIds(new Set(allIds));
  };

  const clearSelection = () => {
    setSelectedMediaIds(new Set());
    setIsMultiSelectMode(false);
  };

  const handleDeleteSelected = async () => {
    if (selectedMediaIds.size === 0) return;
    if (!confirm(`Delete ${selectedMediaIds.size} selected file(s)?`)) return;

    try {
      const deletePromises = Array.from(selectedMediaIds).map(mediaId =>
        api.delete(`/media/${mediaId}`)
      );
      await Promise.all(deletePromises);
      toast.success(`${selectedMediaIds.size} file(s) deleted`);
      setSelectedMediaIds(new Set());
      setIsMultiSelectMode(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete some files');
    }
  };

  const toggleFolderExpand = (folderId) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };

  const [rewritingUrls, setRewritingUrls] = useState(false);
  const handleRewriteUrls = async () => {
    setRewritingUrls(true);
    try {
      const res = await api.post('/media/rewrite-urls');
      const d = res.data;
      const slugMsg = d.slugs_backfilled > 0
        ? ` Generated pretty URLs for ${d.slugs_backfilled} older file${d.slugs_backfilled === 1 ? '' : 's'}.`
        : '';
      toast.success(
        `Done! Updated ${d.media_updated} media files, ${d.articles_updated} articles, ${d.pages_updated} pages to use your domain.${slugMsg}`
      );
      fetchData();
    } catch (error) {
      toast.error('Failed to update URLs');
    } finally {
      setRewritingUrls(false);
    }
  };

  // Build folder tree
  const buildFolderTree = (parentId = null) => {
    return folders
      .filter(f => f.parent_id === parentId)
      .map(folder => ({
        ...folder,
        children: buildFolderTree(folder.folder_id)
      }));
  };

  const folderTree = buildFolderTree();

  // Get current folder path
  const getFolderPath = (folderId) => {
    const path = [];
    let current = folders.find(f => f.folder_id === folderId);
    while (current) {
      path.unshift(current);
      current = folders.find(f => f.folder_id === current.parent_id);
    }
    return path;
  };

  const currentPath = currentFolderId ? getFolderPath(currentFolderId) : [];

  // Quick lookup of folder name by id for "in <folder>" pill on search results
  const folderNameById = (folderId) => {
    if (!folderId) return 'Root';
    const f = folders.find((x) => x.folder_id === folderId);
    return f ? f.name : 'Root';
  };

  // Filter media:
  //   • empty search → current-folder view (existing behaviour).
  //   • non-empty search → match across ALL folders (most useful when the
  //     user can't remember which folder they put a file in).
  const trimmedQuery = searchQuery.trim().toLowerCase();
  const isSearching = trimmedQuery.length > 0;
  const matchesQuery = (item) => {
    if (!isSearching) return true;
    const haystacks = [
      item.filename,
      item.original_filename,
      item.alt_text,
      item.title,
      item.caption,
      item.custom_slug,
    ];
    return haystacks.some((s) => typeof s === 'string' && s.toLowerCase().includes(trimmedQuery));
  };
  const filteredMedia = media.filter((item) => {
    if (isSearching) return matchesQuery(item);
    // Root view shows ALL media across every folder (sorted newest-first by
    // the backend). Folder views stay scoped to that folder's contents.
    if (!currentFolderId) return true;
    return item.folder_id === currentFolderId;
  });

  // Apply the active sort order (default = backend's newest-first).
  const sortedMedia = (() => {
    const arr = [...filteredMedia];
    const tsOf = (m) => new Date(m.uploaded_at || 0).getTime();
    const nameOf = (m) => (m.filename || '').toLowerCase();
    const sizeOf = (m) => Number(m.size || 0);
    switch (sortOrder) {
      case 'oldest': arr.sort((a, b) => tsOf(a) - tsOf(b)); break;
      case 'name-asc': arr.sort((a, b) => nameOf(a).localeCompare(nameOf(b))); break;
      case 'name-desc': arr.sort((a, b) => nameOf(b).localeCompare(nameOf(a))); break;
      case 'size-desc': arr.sort((a, b) => sizeOf(b) - sizeOf(a)); break;
      case 'size-asc': arr.sort((a, b) => sizeOf(a) - sizeOf(b)); break;
      case 'newest':
      default: arr.sort((a, b) => tsOf(b) - tsOf(a)); break;
    }
    return arr;
  })();

  // Slice the filtered list into the current page for rendering.
  const totalPages = Math.max(1, Math.ceil(sortedMedia.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pagedMedia = sortedMedia.slice(pageStart, pageStart + PAGE_SIZE);

  // Render folder tree item
  const renderFolderItem = (folder, depth = 0) => {
    const hasChildren = folder.children && folder.children.length > 0;
    const isExpanded = expandedFolders[folder.folder_id];
    const isActive = currentFolderId === folder.folder_id;

    return (
      <div key={folder.folder_id}>
        <div
          className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer group ${
            isActive ? 'bg-indigo-100 text-indigo-700' : 'hover:bg-slate-100'
          }`}
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
        >
          {hasChildren ? (
            <button onClick={() => toggleFolderExpand(folder.folder_id)} className="p-0.5">
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          ) : (
            <span className="w-5" />
          )}
          <Folder size={16} className={isActive ? 'text-indigo-600' : 'text-slate-400'} />
          <span 
            className="flex-1 text-sm truncate"
            title={folder.name}
            onClick={() => setCurrentFolderId(folder.folder_id)}
          >
            {folder.name}
          </span>
          <div className="opacity-0 group-hover:opacity-100 flex gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedFolder(folder);
                setNewFolderName(folder.name);
                setShowRenameFolderModal(true);
              }}
              className="p-1 hover:bg-slate-200 rounded"
              title="Rename"
            >
              <Edit2 size={12} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteFolder(folder.folder_id);
              }}
              className="p-1 hover:bg-red-100 text-red-500 rounded"
              title="Delete"
            >
              <Trash2 size={12} />
            </button>
          </div>
        </div>
        {hasChildren && isExpanded && (
          <div>
            {folder.children.map(child => renderFolderItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex" data-testid="media-library-page">
      <Sidebar />
      
      <div className="flex-1 min-w-0 bg-slate-50 flex">
        {/* Folder Sidebar */}
        <div className="w-64 bg-white border-r border-slate-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-sm text-slate-600">Folders</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setNewFolderParentId(currentFolderId);
                setShowNewFolderModal(true);
              }}
              className="h-7 w-7 p-0"
              title="New Folder"
            >
              <FolderPlus size={16} />
            </Button>
          </div>
          
          {/* Root folder */}
          <div
            className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer mb-1 ${
              currentFolderId === null ? 'bg-indigo-100 text-indigo-700' : 'hover:bg-slate-100'
            }`}
            onClick={() => setCurrentFolderId(null)}
          >
            <Folder size={16} className={currentFolderId === null ? 'text-indigo-600' : 'text-slate-400'} />
            <span className="text-sm">All Media</span>
          </div>

          {/* Folder tree */}
          <div className="mt-2">
            {folderTree.map(folder => renderFolderItem(folder))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0 p-8">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <div>
                <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Media Library
                </h1>
                {/* Breadcrumb */}
                <div className="flex items-center gap-1 mt-2 text-sm text-slate-500">
                  <button onClick={() => setCurrentFolderId(null)} className="hover:text-indigo-600">
                    Root
                  </button>
                  {currentPath.map((folder, idx) => (
                    <React.Fragment key={folder.folder_id}>
                      <ChevronRight size={14} />
                      <button
                        onClick={() => setCurrentFolderId(folder.folder_id)}
                        className={idx === currentPath.length - 1 ? 'text-slate-700 font-medium' : 'hover:text-indigo-600'}
                        title={folder.name}
                      >
                        {folder.name}
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                {/* Multi-select controls */}
                {selectedMediaIds.size > 0 ? (
                  <>
                    <span className="flex items-center text-sm text-slate-600 mr-2">
                      {selectedMediaIds.size} selected
                    </span>
                    <Button
                      variant="outline"
                      onClick={() => setShowMoveModal(true)}
                    >
                      <Move size={16} className="mr-2" />
                      Move Selected
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleDeleteSelected}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 size={16} className="mr-2" />
                      Delete
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={clearSelection}
                    >
                      <X size={16} className="mr-1" />
                      Clear
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant={isMultiSelectMode ? "secondary" : "outline"}
                      onClick={() => setIsMultiSelectMode(!isMultiSelectMode)}
                    >
                      <CheckSquare size={16} className="mr-2" />
                      {isMultiSelectMode ? "Exit Select" : "Select Multiple"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleRewriteUrls}
                      disabled={rewritingUrls}
                      title="Update all file URLs to use your site's domain, and auto-generate pretty /documents/<slug> URLs for any older files that don't have one yet."
                    >
                      <RefreshCw size={16} className={`mr-2 ${rewritingUrls ? 'animate-spin' : ''}`} />
                      {rewritingUrls ? 'Updating...' : 'Fix URLs'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setNewFolderParentId(currentFolderId);
                        setShowNewFolderModal(true);
                      }}
                    >
                      <FolderPlus size={18} className="mr-2" />
                      New Folder
                    </Button>
                  </>
                )}
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  onChange={handleFileInputChange}
                  disabled={uploading}
                  multiple
                />
                <label htmlFor="file-upload">
                  <Button asChild className="bg-purple-600 hover:bg-purple-700" data-testid="upload-button">
                    <span className="cursor-pointer">
                      <Upload size={18} className="mr-2" />
                      {uploading ? 'Uploading...' : 'Upload Files'}
                    </span>
                  </Button>
                </label>
              </div>
            </div>

            {/* Search Bar — searches filename / title / alt text / caption / slug
                across ALL folders when something is typed. Empty = folder-scoped.
                The Sort dropdown sits inline so editors can re-order the listing
                without leaving the toolbar. */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search all media — filename, alt text, title, caption…"
                  className="pl-9 pr-10"
                  data-testid="media-search-input"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                    title="Clear search"
                    data-testid="media-search-clear"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Label htmlFor="media-sort" className="text-xs text-slate-500 whitespace-nowrap">Sort by</Label>
                <Select value={sortOrder} onValueChange={setSortOrder}>
                  <SelectTrigger
                    id="media-sort"
                    className="h-9 w-[170px]"
                    data-testid="media-sort-trigger"
                  >
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest" data-testid="media-sort-newest">Newest first</SelectItem>
                    <SelectItem value="oldest" data-testid="media-sort-oldest">Oldest first</SelectItem>
                    <SelectItem value="name-asc" data-testid="media-sort-name-asc">Name A – Z</SelectItem>
                    <SelectItem value="name-desc" data-testid="media-sort-name-desc">Name Z – A</SelectItem>
                    <SelectItem value="size-desc" data-testid="media-sort-size-desc">Largest first</SelectItem>
                    <SelectItem value="size-asc" data-testid="media-sort-size-asc">Smallest first</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {isSearching && (
              <p className="text-sm text-slate-500 mb-3" data-testid="media-search-result-count">
                {filteredMedia.length} {filteredMedia.length === 1 ? 'result' : 'results'} for
                <span className="font-medium text-slate-700"> "{searchQuery}"</span> across all folders
              </p>
            )}

            {/* Drop Zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-8 mb-6 text-center transition-all ${
                dragOver 
                  ? 'border-indigo-500 bg-indigo-50' 
                  : 'border-slate-300 hover:border-slate-400'
              }`}
            >
              <Upload size={40} className={`mx-auto mb-3 ${dragOver ? 'text-indigo-500' : 'text-slate-400'}`} />
              <p className={`text-lg font-medium ${dragOver ? 'text-indigo-600' : 'text-slate-600'}`}>
                {dragOver ? 'Drop files here' : 'Drag & drop files here'}
              </p>
              <p className="text-sm text-slate-500 mt-1">or use the upload button above</p>
            </div>

            {/* Multi-select bar */}
            {isMultiSelectMode && filteredMedia.length > 0 && (
              <div className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2 mb-4">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      if (selectedMediaIds.size === filteredMedia.length) {
                        setSelectedMediaIds(new Set());
                      } else {
                        selectAllInFolder();
                      }
                    }}
                    className="flex items-center gap-2 text-sm text-indigo-700 hover:text-indigo-900"
                  >
                    {selectedMediaIds.size === filteredMedia.length ? (
                      <CheckSquare size={18} />
                    ) : (
                      <Square size={18} />
                    )}
                    {selectedMediaIds.size === filteredMedia.length ? 'Deselect All' : 'Select All'}
                  </button>
                  <span className="text-sm text-slate-500">
                    {filteredMedia.length} {currentFolderId ? 'items in folder' : 'items total'}
                  </span>
                </div>
                <p className="text-xs text-indigo-600">Click images to select them</p>
              </div>
            )}

            {/* Media Grid */}
            {loading ? (
              <div className="text-center py-12">
                <p className="text-slate-500">Loading media...</p>
              </div>
            ) : filteredMedia.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <Upload size={48} className="mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500 mb-4">
                  {isSearching
                    ? `No media matches "${searchQuery}".`
                    : (currentFolderId ? 'No media files in this folder' : 'No media files yet — upload your first image to get started.')}
                </p>
              </div>
            ) : (
              <>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {pagedMedia.map((item) => {
                  const isSelected = selectedMediaIds.has(item.media_id);
                  return (
                  <div
                    key={item.media_id}
                    data-testid={`media-item-${item.media_id}`}
                    className={`bg-white border rounded-lg p-3 hover:shadow-lg transition-all group relative ${
                      isSelected ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-slate-200'
                    }`}
                    onClick={(e) => {
                      if (isMultiSelectMode) {
                        e.stopPropagation();
                        toggleMediaSelection(item.media_id);
                      }
                    }}
                  >
                    {/* Selection checkbox */}
                    {(isMultiSelectMode || isSelected) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleMediaSelection(item.media_id);
                        }}
                        className={`absolute top-2 left-2 z-10 w-6 h-6 rounded flex items-center justify-center transition-all ${
                          isSelected 
                            ? 'bg-indigo-600 text-white' 
                            : 'bg-white/90 border border-slate-300 hover:border-indigo-400'
                        }`}
                      >
                        {isSelected && <Check size={14} />}
                      </button>
                    )}
                    
                    <div 
                      className={`aspect-square bg-slate-100 rounded mb-2 flex items-center justify-center relative ${!isMultiSelectMode ? 'cursor-pointer' : ''}`}
                      onClick={(e) => {
                        if (!isMultiSelectMode && item.mime_type?.startsWith('image/')) {
                          e.stopPropagation();
                          setPreviewImage(item);
                          setShowImagePreview(true);
                        }
                      }}
                    >
                      {item.mime_type?.startsWith('image/') ? (
                        <>
                          <img src={item.url} alt={item.filename} className="w-full h-full object-cover rounded" />
                          {!isMultiSelectMode && (
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center rounded">
                              <ZoomIn size={24} className="text-white opacity-0 group-hover:opacity-100" />
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="text-4xl text-slate-300">📄</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 truncate mb-1" title={item.filename}>{item.filename}</p>
                    {/* When searching across folders, show where this file lives + a quick "jump to folder" affordance */}
                    {isSearching && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentFolderId(item.folder_id || null);
                          setSearchQuery('');
                        }}
                        className="text-[11px] text-indigo-600 hover:text-indigo-800 truncate mb-1 flex items-center gap-1 max-w-full"
                        title={`Open folder: ${folderNameById(item.folder_id)}`}
                        data-testid={`media-result-folder-${item.media_id}`}
                      >
                        <Folder size={10} />
                        <span className="truncate">{folderNameById(item.folder_id)}</span>
                      </button>
                    )}
                    {/* Show alt text indicator if set */}
                    {item.alt_text && (
                      <p className="text-xs text-green-600 truncate mb-1 flex items-center gap-1">
                        <FileText size={10} />
                        Alt text set
                      </p>
                    )}
                    {!isMultiSelectMode && (
                      <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity overflow-hidden">
                      {/* Copy URL button - always visible on hover */}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 w-full text-xs bg-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(getPreferredUrl(item));
                          toast.success(item.custom_slug ? 'Pretty URL copied' : 'URL copied to clipboard');
                        }}
                        data-testid={`copy-url-${item.media_id}`}
                      >
                        <Copy size={12} className="mr-1" />
                        Copy URL
                      </Button>
                      <div className="flex gap-1 min-w-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 flex-1 min-w-0 text-xs px-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditMetadata(item);
                        }}
                        data-testid={`edit-metadata-${item.media_id}`}
                      >
                        <Edit2 size={12} className="mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 flex-1 min-w-0 text-xs px-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedMedia(item);
                          setShowMoveModal(true);
                        }}
                      >
                        <Move size={12} className="mr-1" />
                        Move
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-9 shrink-0 text-red-500 hover:text-red-700 hover:bg-red-50 px-0"
                        onClick={() => handleDelete(item.media_id)}
                        data-testid={`delete-media-${item.media_id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                      </div>
                      </div>
                    )}
                  </div>
                );
                })}
              </div>

              {/* Pagination control — only shown when there are more items than fit on one page */}
              {totalPages > 1 && (
                <div
                  className="flex items-center justify-between mt-6 px-1"
                  data-testid="media-pagination"
                >
                  <p className="text-xs text-slate-500" data-testid="media-pagination-status">
                    Showing <span className="font-medium text-slate-700">{pageStart + 1}</span>
                    {'–'}
                    <span className="font-medium text-slate-700">
                      {Math.min(pageStart + PAGE_SIZE, filteredMedia.length)}
                    </span>
                    {' '}of <span className="font-medium text-slate-700">{filteredMedia.length}</span>
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={safePage <= 1}
                      data-testid="media-pagination-prev"
                    >
                      Previous
                    </Button>
                    <span className="text-xs text-slate-600 px-2">
                      Page <span className="font-medium">{safePage}</span> of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={safePage >= totalPages}
                      data-testid="media-pagination-next"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* New Folder Modal */}
      <Dialog open={showNewFolderModal} onOpenChange={setShowNewFolderModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Folder Name</label>
              <Input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Enter folder name"
                className="mt-1"
                autoFocus
              />
            </div>
            <div>
              <label className="text-sm font-medium">Parent Folder</label>
              <Select
                value={newFolderParentId || 'root'}
                onValueChange={(value) => setNewFolderParentId(value === 'root' ? null : value)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select parent folder" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="root">Root (No parent)</SelectItem>
                  {folders.map(folder => (
                    <SelectItem key={folder.folder_id} value={folder.folder_id}>
                      {folder.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewFolderModal(false)}>Cancel</Button>
            <Button onClick={handleCreateFolder} className="bg-indigo-600 hover:bg-indigo-700">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Folder Modal */}
      <Dialog open={showRenameFolderModal} onOpenChange={setShowRenameFolderModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Folder</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium">New Name</label>
            <Input
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Enter new folder name"
              className="mt-1"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRenameFolderModal(false)}>Cancel</Button>
            <Button onClick={handleRenameFolder} className="bg-indigo-600 hover:bg-indigo-700">Rename</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Media Modal */}
      <Dialog open={showMoveModal} onOpenChange={setShowMoveModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move Media</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              {selectedMediaIds.size > 0 
                ? `Select destination folder for ${selectedMediaIds.size} selected file(s)`
                : <>Select destination folder for: <strong>{selectedMedia?.filename}</strong></>
              }
            </p>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              <button
                onClick={() => handleMoveMedia(null)}
                className="w-full flex items-center gap-2 px-3 py-2 rounded hover:bg-slate-100 text-left"
              >
                <Folder size={16} className="text-slate-400" />
                <span>Root (No folder)</span>
              </button>
              {folders.map(folder => (
                <button
                  key={folder.folder_id}
                  onClick={() => handleMoveMedia(folder.folder_id)}
                  disabled={selectedMediaIds.size === 0 && selectedMedia?.folder_id === folder.folder_id}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded hover:bg-slate-100 text-left disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Folder size={16} className="text-slate-400" />
                  <span>{folder.name}</span>
                  {selectedMediaIds.size === 0 && selectedMedia?.folder_id === folder.folder_id && (
                    <span className="text-xs text-slate-400">(current)</span>
                  )}
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMoveModal(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview Modal */}
      <Dialog open={showImagePreview} onOpenChange={setShowImagePreview}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          <button
            onClick={() => setShowImagePreview(false)}
            className="absolute top-4 right-4 z-10 p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-70"
          >
            <X size={20} />
          </button>
          {previewImage && (
            <div className="relative">
              <img
                src={previewImage.url}
                alt={previewImage.alt_text || previewImage.filename}
                className="w-full h-auto max-h-[80vh] object-contain bg-slate-900"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                <p className="text-white font-medium">{previewImage.filename}</p>
                <p className="text-white/70 text-sm">
                  {(previewImage.size / 1024).toFixed(1)} KB • {previewImage.mime_type}
                </p>
                {previewImage.alt_text && (
                  <p className="text-white/50 text-xs mt-1">Alt: {previewImage.alt_text}</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Metadata Modal */}
      <Dialog open={showEditMetadataModal} onOpenChange={setShowEditMetadataModal}>
        <DialogContent className="max-w-lg overflow-hidden">
          <DialogHeader className="pr-8">
            <DialogTitle className="flex items-center gap-2">
              {editingMedia?.mime_type?.startsWith('image/') ? (
                <><Image size={20} className="text-indigo-600" />Edit Image Metadata</>
              ) : (
                <><FileText size={20} className="text-indigo-600" />Edit File Details</>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {editingMedia && (
            <div className="space-y-4 min-w-0 max-h-[70vh] overflow-y-auto pr-1">
              {/* Preview */}
              {editingMedia.mime_type?.startsWith('image/') ? (
                <div className="w-full h-40 bg-slate-100 rounded-lg overflow-hidden">
                  <img 
                    src={editingMedia.url} 
                    alt={editingMedia.filename}
                    className="w-full h-full object-contain"
                  />
                </div>
              ) : (
                <div className="w-full h-32 bg-slate-100 rounded-lg flex flex-col items-center justify-center gap-2">
                  <FileText size={40} className="text-slate-400" />
                  <span className="text-xs uppercase tracking-wide text-slate-500">
                    {(editingMedia.mime_type || 'file').split('/').pop()}
                  </span>
                </div>
              )}
              <p className="text-sm text-slate-500 text-center truncate" title={editingMedia.filename}>{editingMedia.filename}</p>

              {/* URL with Copy Button — shows pretty URL when available */}
              <div className="flex items-center gap-2 p-2 bg-slate-100 rounded-lg min-w-0">
                <Link size={14} className="text-slate-400 flex-shrink-0" />
                <code className="text-xs text-slate-600 truncate flex-1 min-w-0" title={getPreferredUrl(editingMedia)}>{getPreferredUrl(editingMedia)}</code>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs flex-shrink-0"
                  onClick={() => {
                    navigator.clipboard.writeText(getPreferredUrl(editingMedia));
                    toast.success(editingMedia.custom_slug ? 'Pretty URL copied' : 'URL copied to clipboard');
                  }}
                >
                  <Copy size={12} className="mr-1" />
                  Copy
                </Button>
              </div>

              {/* Alt Text */}
              {editingMedia.mime_type?.startsWith('image/') && (
              <div>
                <Label htmlFor="alt-text" className="text-sm font-medium">
                  Alt Text <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="alt-text"
                  value={mediaMetadata.alt_text}
                  onChange={(e) => setMediaMetadata(prev => ({ ...prev, alt_text: e.target.value }))}
                  placeholder="Describe the image for screen readers and SEO (e.g., 'Team members discussing project in meeting room')"
                  className="mt-1 h-20 resize-none"
                  data-testid="media-alt-text-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Important for accessibility and SEO. Describe what's in the image.
                </p>
              </div>
              )}

              {/* Title */}
              {editingMedia.mime_type?.startsWith('image/') && (
              <div>
                <Label htmlFor="image-title" className="text-sm font-medium">
                  Title (Optional)
                </Label>
                <Input
                  id="image-title"
                  value={mediaMetadata.title}
                  onChange={(e) => setMediaMetadata(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Image title (shown on hover)"
                  className="mt-1"
                  data-testid="media-title-input"
                />
              </div>
              )}

              {/* Caption */}
              {editingMedia.mime_type?.startsWith('image/') && (
              <div>
                <Label htmlFor="image-caption" className="text-sm font-medium">
                  Caption (Optional)
                </Label>
                <Textarea
                  id="image-caption"
                  value={mediaMetadata.caption}
                  onChange={(e) => setMediaMetadata(prev => ({ ...prev, caption: e.target.value }))}
                  placeholder="Caption to display below the image"
                  className="mt-1 h-16 resize-none"
                  data-testid="media-caption-input"
                />
              </div>
              )}

              {/* Custom URL slug */}
              <div className="border-t pt-4">
                <Label htmlFor="custom-slug" className="text-sm font-medium">
                  Custom URL
                </Label>
                <p className="text-xs text-slate-500 mt-1 mb-2">
                  Give this file a clean public URL. If you change it later, the old URL will automatically redirect to the new one — existing links keep working.
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 whitespace-nowrap">/documents/</span>
                  <Input
                    id="custom-slug"
                    value={slugInput}
                    onChange={(e) => setSlugInput(e.target.value)}
                    placeholder="annual-report-2026"
                    className="flex-1 font-mono text-sm"
                    data-testid="media-slug-input"
                  />
                  <Button
                    size="sm"
                    onClick={handleSaveSlug}
                    disabled={savingSlug || !slugInput.trim() || slugInput.trim() === (editingMedia.custom_slug || '')}
                    data-testid="media-save-slug-btn"
                  >
                    {savingSlug ? 'Saving…' : 'Save URL'}
                  </Button>
                </div>
                {editingMedia.custom_slug && (
                  <div className="flex flex-wrap items-center gap-2 p-2 mt-2 bg-indigo-50 rounded-lg min-w-0">
                    <Link size={14} className="text-indigo-500 flex-shrink-0" />
                    <code className="text-xs text-indigo-700 truncate flex-1 min-w-0" title={`/documents/${editingMedia.custom_slug}`}>
                      /documents/{editingMedia.custom_slug}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs flex-shrink-0"
                      onClick={() => {
                        navigator.clipboard.writeText(getPreferredUrl(editingMedia));
                        toast.success('Pretty URL copied to clipboard');
                      }}
                      data-testid="media-copy-pretty-url-btn"
                    >
                      <Copy size={12} className="mr-1" />
                      Copy
                    </Button>
                    {/* Force-download link — only useful when the file is inline-friendly (PDF / image) */}
                    {(() => {
                      const mime = (editingMedia.mime_type || '').toLowerCase();
                      const inlineFriendly = mime === 'application/pdf' || mime.startsWith('image/');
                      if (!inlineFriendly) return null;
                      const pretty = getPreferredUrl(editingMedia);
                      const downloadUrl = pretty + (pretty.includes('?') ? '&' : '?') + 'download=1';
                      return (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs flex-shrink-0"
                          onClick={() => {
                            navigator.clipboard.writeText(downloadUrl);
                            toast.success('Download link copied — opens the browser save dialog');
                          }}
                          data-testid="media-copy-download-url-btn"
                          title="Copy a force-download link (adds ?download=1)"
                        >
                          <Copy size={12} className="mr-1" />
                          Copy download link
                        </Button>
                      );
                    })()}
                  </div>
                )}
                {Array.isArray(editingMedia.slug_history) && editingMedia.slug_history.length > 0 && (
                  <div className="mt-2 text-xs text-slate-500">
                    <p className="font-medium mb-1">Previous URLs (auto-redirected):</p>
                    <ul className="space-y-0.5 pl-4 list-disc">
                      {editingMedia.slug_history.map((h, idx) => (
                        <li key={`${h.slug}-${idx}`} className="font-mono">
                          /documents/{h.slug}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowEditMetadataModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveMetadata} 
              disabled={savingMetadata}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {savingMetadata ? 'Saving...' : 'Save Metadata'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MediaLibrary;
